/** A group as the header sees it: its skip days, by scope, and -- when the
 *  roster has loaded -- its three visible days and each student's lessons on
 *  them. */
export interface SkipDays {
  /** Every skipped day, including "selected students only" skips. */
  skipped_dates?: string[];
  /** Days the whole group is off. Absent from older APIs. */
  group_skipped_dates?: string[];
  previous_date?: string | null;
  current_date?: string | null;
  next_date?: string | null;
  students?: ReadonlyArray<{
    student_id?: number;
    previous: ReadonlyArray<{ status?: string }>;
    current: ReadonlyArray<{ status?: string }>;
    upcoming: ReadonlyArray<{ status?: string }>;
  }>;
}

/**
 * Does the roster show any lesson for this group on `date`?
 *
 * A skip is a record of a day off, not proof the day is empty: Change lesson
 * and the starting-point review place the chosen lesson on today whatever the
 * calendar says, so a group can skip a day and still hold a lesson on it --
 * GDS Group B on 2026-10-01, three skips in the header and two of those
 * students with a lesson that day. The header counts what the teacher sees.
 * A date outside the group's three visible days, or a group with no roster
 * loaded, reads as empty, which is what the header assumed before.
 */
export const holdsLessonsOn = (group: SkipDays, date: string): boolean =>
  studentsOn(group, date).length > 0;

/** Statuses `shift_rows` leaves where they are: settled work, and the pile. */
const UNMOVED_STATUSES = new Set(["finished", "incomplete", "skipped"]);

/**
 * The students with a lesson on `date` that a skip would move.
 *
 * Narrower than `holdsLessonsOn`, which counts what the teacher sees: a
 * finished or piled lesson stays on its day through a skip, and a student
 * skipped for one would only have their later work delayed.
 */
export const studentsWithMovableLessonsOn = (
  group: SkipDays,
  date: string,
): number[] =>
  studentsOn(
    group,
    date,
    (lesson) => !UNMOVED_STATUSES.has(lesson.status ?? ""),
  ).flatMap((student) =>
    student.student_id === undefined ? [] : [student.student_id],
  );

const studentsOn = (
  group: SkipDays,
  date: string,
  counts: (lesson: { status?: string }) => boolean = () => true,
) => {
  const column =
    date === group.previous_date
      ? "previous"
      : date === group.current_date
        ? "current"
        : date === group.next_date
          ? "upcoming"
          : null;
  if (!column || !group.students) return [];
  return group.students.filter((student) => student[column].some(counts));
};

/**
 * Does a group with this pace ever teach on `date`'s weekday? The backend's
 * `is_sequencing_day`: the first `daysPerWeek` weekdays absorb the week, and
 * the later ones never receive a lesson. A skip there is not a no-op --
 * `skip_day` still shifts every row after it -- so such a group is skipped
 * only for the students with a lesson kept on that day.
 */
export const servesWeekday = (daysPerWeek: number, date: string): boolean => {
  const weekday = (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7;
  return weekday < Math.max(1, Math.min(daysPerWeek, 5));
};

/**
 * A group's own today: its focused column walked back by its own offset.
 *
 * Per group, because each roster is computed in its own organization's
 * timezone -- two groups on screen can be a day apart near midnight, and the
 * API refuses a skip on a day already past for that group. In UTC
 * throughout: a local midnight read back with `toISOString` is the previous
 * day east of Greenwich.
 *
 * Both halves from the group's own response, never the page's `dayOffset`:
 * that moves on click and `current_date` only when the response lands, so
 * mixing them put today wrong by the delta while a page was in flight, and a
 * fast click could offer a skip the API then refused as past.
 */
export const groupToday = (group: {
  current_date?: string | null;
  day_offset?: number;
}): string | null => {
  if (!group.current_date) return null;
  const focus = new Date(`${group.current_date}T00:00:00Z`);
  const offset = group.day_offset ?? 0;
  let remaining = Math.abs(offset);
  const step = offset > 0 ? -1 : 1;
  while (remaining > 0) {
    focus.setUTCDate(focus.getUTCDate() + step);
    if (focus.getUTCDay() !== 0 && focus.getUTCDay() !== 6) remaining -= 1;
  }
  return focus.toISOString().slice(0, 10);
};

/** How the groups on screen skip one date. */
export interface SkipCoverage {
  /** Groups with the whole day off. */
  off: number;
  /** Groups with any skip that day, whole-group or selected students. */
  touched: number;
  total: number;
}

/**
 * The roster's date header speaks for every group on screen, not only the
 * first, and counts a group only when its day is empty (`holdsLessonsOn`). Reading `primary.skipped_dates` alone tagged a column "No school"
 * whenever the first selected group had the day off, while the other groups
 * still had lessons in it: GDS Group B on 2026-09-26, where Adam's Tuesday and
 * Wednesday skips headed a view with Freddie's lessons on both days.
 *
 * Whole-group skips are counted apart from selected-student ones, because
 * `skipped_dates` merges the two and one student's day off is not the group's.
 * An API without `group_skipped_dates` falls back to it, as before.
 */
export const skipCoverage = (
  groups: ReadonlyArray<SkipDays>,
  date?: string | null,
): SkipCoverage => {
  if (!date) return { off: 0, touched: 0, total: groups.length };
  const wholeGroup = (group: SkipDays) =>
    Boolean((group.group_skipped_dates ?? group.skipped_dates)?.includes(date));
  // Emptiness is asked of a whole-group skip only. A "selected students only"
  // skip leaves their classmates' lessons in the column by design, and is
  // still the teacher's to see and remove.
  const wholeAndEmpty = (group: SkipDays) =>
    wholeGroup(group) && !holdsLessonsOn(group, date);
  return {
    off: groups.filter(wholeAndEmpty).length,
    touched: groups.filter(
      (group) =>
        Boolean(group.skipped_dates?.includes(date)) &&
        // An API without `group_skipped_dates` cannot tell a whole-group skip
        // from a student's, and a student's leaves lessons in the column by
        // design: such a response keeps the plain count it always had.
        (group.group_skipped_dates === undefined ||
          !wholeGroup(group) ||
          wholeAndEmpty(group)),
    ).length,
    total: groups.length,
  };
};

/** Every group on screen has the whole day off: the column is no school. */
export const isWholeDaySkipped = ({ off, total }: SkipCoverage) =>
  total > 0 && off === total;

/** The header's tag for a date, or null when no group on screen skips it. */
export const skipTag = (coverage: SkipCoverage): string | null => {
  if (isWholeDaySkipped(coverage)) return "Skipped · No school";
  if (!coverage.touched) return null;
  return coverage.total === 1
    ? "Skipped · Some students"
    : `Skips in ${coverage.touched} of ${coverage.total} groups`;
};
